import { FACILITY_DISPLAY_NAME_MAX_LENGTH, z } from '@dcp/shared'

/**
 * Shared Zod schema for the Add and Edit Facility forms.
 *
 * The Add (`AddFacilityDialog`) and Edit (`EditFacilityDialog`) modals share
 * the same field set and validation contract — both bind the same five
 * editable columns (`legal_name`, `name`, `sage_location_id`, `code`,
 * `is_active`) and need identical client-side rules:
 *
 *   - `legal_name`        : the Facility Legal Name (ADR 0021). Required on
 *                            every save, including facilities whose Legal Name
 *                            the Sage sync has not filled yet. Same trim / 255 cap.
 *   - `name`              : the Display Name; required, whitespace-only rejected
 *                            via `.trim().min(1, …)`. Capped at
 *                            FACILITY_DISPLAY_NAME_MAX_LENGTH (40, product rule):
 *                            it is the short label, the full name goes in Legal Name. Uniqueness of both names
 *                            is the database's job; the dialogs show its `23505`.
 *   - `sage_location_id`  : required (DCP-154 product decision — every
 *                            new/edited facility must carry the Sage
 *                            mapping; legacy NULL rows are read-only safe
 *                            but the moment they're opened in Edit they
 *                            must be filled). Capped at 255 chars.
 *   - `code`              : optional free-text; `.trim()` only so blank
 *                            input becomes `''` and is converted to `null`
 *                            by the caller via `normalizeOptionalFacilityField`.
 *                            Capped at 255 chars.
 *   - `is_active`         : boolean toggle.
 *
 * Pack membership is intentionally NOT part of this schema: a facility's pack
 * is assigned from the Packs settings page (DCP-171), not the facility form.
 *
 * **Length caps.** The underlying `facilities` columns are PostgreSQL
 * `TEXT` (no hard length limit), but unbounded user input is a foot-gun
 * — a pasted multi-KB tooltip or an accidentally-attached email body
 * would otherwise sail through to the DB, bloat the table cell, and
 * potentially break downstream rendering / reporting. 255 chars matches
 * the conventional legacy `VARCHAR(255)` ceiling, comfortably exceeds
 * every realistic facility name or Sage location id we've seen, and
 * surfaces the failure as a precise Zod error at the form layer instead
 * of as a confusing server-side reject.
 *
 * Single source of truth so a future field addition (description, code
 * format constraint, etc.) lands in one place and both dialogs pick it up
 * via shared `z.infer`. The previous shape duplicated this schema inline
 * in `EditFacilityDialog.tsx` and the Add modal didn't use Zod at all
 * (raw `useState` + manual `trim()` in the submit handler), which
 * contradicted the PR title's claim of a *symmetric* add/edit form.
 *
 * @see EditFacilityDialog - consumes via `zodResolver(facilityFormSchema)`.
 * @see AddFacilityDialog  - consumes via `zodResolver(facilityFormSchema)`.
 */
const MAX_FACILITY_FIELD_LENGTH = 255

export const facilityFormSchema = z.object({
  legal_name: z
    .string()
    .trim()
    .min(1, 'Facility Legal Name is required')
    .max(
      MAX_FACILITY_FIELD_LENGTH,
      `Facility Legal Name must be ${MAX_FACILITY_FIELD_LENGTH} characters or fewer`,
    ),
  name: z
    .string()
    .trim()
    .min(1, 'Display Name is required')
    .max(
      FACILITY_DISPLAY_NAME_MAX_LENGTH,
      `Display Name must be ${FACILITY_DISPLAY_NAME_MAX_LENGTH} characters or fewer — use Facility Legal Name for the full name.`,
    ),
  sage_location_id: z
    .string()
    .trim()
    .min(1, 'Sage Facility ID is required')
    // Mirror the DB CHECK constraint `facilities_sage_location_id_format`
    // (migration 20260522000000): NULL or `^[A-Za-z0-9_-]{1,100}$`. Enforcing
    // the same shape here turns what would otherwise surface as an opaque
    // server-side `check_violation` into a precise field-level error at submit
    // time. Length cap is 100 (Sage's LOCATIONID limit), not the generic 255.
    .max(100, 'Sage Facility ID must be 100 characters or fewer')
    .regex(
      /^[A-Za-z0-9_-]+$/,
      'Sage Facility ID may only contain letters, digits, "_" or "-"',
    ),
  code: z
    .string()
    .trim()
    .max(MAX_FACILITY_FIELD_LENGTH, `Code must be ${MAX_FACILITY_FIELD_LENGTH} characters or fewer`),
  is_active: z.boolean(),
})

/**
 * Form-shape inferred from {@link facilityFormSchema}.
 * Used as the generic argument to `useForm<FacilityFormData>(…)` in both
 * dialogs so the typed `register` / `setValue` calls share the same field
 * names + types.
 */
export type FacilityFormData = z.infer<typeof facilityFormSchema>

/**
 * Normalize an optional facility-form string field: trim whitespace and
 * collapse an empty result to `null` so the DB column stores "no value"
 * rather than an empty string. Shared by the Add and Edit facility flows
 * (e.g. the optional `code` field) to keep the persistence boundary
 * consistent.
 */
export function normalizeOptionalFacilityField(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}
